import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ESTADOS, MOTIVOS_CANCELACION } from '../../lib/constants';
import { Phone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const AdminTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // State change dialog
  const [showStateDialog, setShowStateDialog] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [comentario, setComentario] = useState('');
  const [horaVisita, setHoraVisita] = useState('');
  const [motivoCancelacion, setMotivoCancelacion] = useState('');

  useEffect(() => {
    loadTicket();
  }, [id]);

  const loadTicket = async () => {
    try {
      const response = await ticketsAPI.getById(id);
      setTicket(response.data);
    } catch (error) {
      toast.error('Error al cargar el ticket');
      navigate('/admin/tickets');
    } finally {
      setLoading(false);
    }
  };

  const openStateChangeDialog = (estado) => {
    setSelectedEstado(estado);
    setComentario('');
    setHoraVisita('');
    setMotivoCancelacion('');
    setShowStateDialog(true);
  };

  const handleUpdateEstado = async () => {
    if (!selectedEstado) return;
    
    // Validate required fields based on state
    if (selectedEstado === 'Ausente' && !horaVisita) {
      toast.error('Debe indicar la hora de la visita');
      return;
    }
    if (selectedEstado === 'Cancelada' && !motivoCancelacion) {
      toast.error('Debe seleccionar un motivo de cancelación');
      return;
    }

    setUpdating(true);
    try {
      const updateData = {
        estado: selectedEstado,
        comentario_estado: comentario || null,
      };
      
      if (selectedEstado === 'Ausente') {
        updateData.hora_visita = horaVisita;
      }
      if (selectedEstado === 'Cancelada') {
        updateData.motivo_cancelacion = motivoCancelacion;
      }

      await ticketsAPI.update(id, updateData);
      toast.success(`Estado actualizado a "${selectedEstado}"`);
      setShowStateDialog(false);
      loadTicket();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    } finally {
      setUpdating(false);
    }
  };

  const getEstadoColor = (estado) => {
    const estadoObj = ESTADOS.find(e => e.value === estado);
    return estadoObj ? estadoObj.color : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="text-center py-8" data-testid="ticket-detail-loading">Cargando ticket...</div>;
  }

  if (!ticket) {
    return <div className="text-center py-8">Ticket no encontrado</div>;
  }

  return (
    <div data-testid="admin-ticket-detail">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/tickets')} className="mb-2" data-testid="btn-back">
            ← Volver
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Ticket {ticket.ticket_id}</h1>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getEstadoColor(ticket.estado)}`} data-testid="ticket-status-badge">
          {ticket.estado}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información del Ciudadano</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Nombre</dt>
                  <dd className="mt-1">{ticket.nombre_ciudadano}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Teléfono</dt>
                  <dd className="mt-1">{ticket.telefono}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Email</dt>
                  <dd className="mt-1">{ticket.email}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Canal preferido</dt>
                  <dd className="mt-1">{ticket.canal_respuesta_preferido || 'No especificado'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ubicación</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div className="col-span-2">
                  <dt className="font-medium text-gray-500">Dirección</dt>
                  <dd className="mt-1">{ticket.direccion}</dd>
                </div>
                {ticket.bloque && (
                  <div>
                    <dt className="font-medium text-gray-500">Bloque</dt>
                    <dd className="mt-1">{ticket.bloque}</dd>
                  </div>
                )}
                {ticket.piso && (
                  <div>
                    <dt className="font-medium text-gray-500">Piso</dt>
                    <dd className="mt-1">{ticket.piso}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-medium text-gray-500">Lugar</dt>
                  <dd className="mt-1">{ticket.lugar}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Items ({ticket.items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {ticket.items?.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-sm">
                      {index + 1}. {item.tipo_enser} - {item.elemento}
                    </div>
                    {item.datos_item && (
                      <div className="text-xs text-gray-600 mt-1">{item.datos_item}</div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {ticket.observaciones && (
            <Card>
              <CardHeader>
                <CardTitle>Observaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">{ticket.observaciones}</p>
              </CardContent>
            </Card>
          )}

          {ticket.resumen_llamada && (
            <Card data-testid="card-resumen-llamada">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-teal-600" />
                  Resumen de la Llamada (Agente de Voz)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 whitespace-pre-line">{ticket.resumen_llamada}</p>
              </CardContent>
            </Card>
          )}

          {/* Historial de Estados */}
          {ticket.historial_estados && ticket.historial_estados.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historial de Estados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ticket.historial_estados.map((hist, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <Badge className={getEstadoColor(hist.estado)}>
                        {hist.estado}
                      </Badge>
                      <div className="flex-1">
                        {hist.comentario && (
                          <p className="text-gray-700">{hist.comentario}</p>
                        )}
                        {hist.hora_visita && (
                          <p className="text-orange-600 text-xs mt-1">Hora visita: {hist.hora_visita}</p>
                        )}
                        {hist.motivo_cancelacion && (
                          <p className="text-red-600 text-xs mt-1">Motivo: {hist.motivo_cancelacion}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {hist.timestamp ? new Date(hist.timestamp).toLocaleString('es-ES') : 
                         hist.fecha ? new Date(hist.fecha).toLocaleString('es-ES') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cambiar Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {ESTADOS.map((estado) => (
                  <Button
                    key={estado.value}
                    variant="outline"
                    onClick={() => openStateChangeDialog(estado.value)}
                    disabled={updating || ticket.estado === estado.value}
                    className={`w-full justify-start ${ticket.estado === estado.value ? 'border-primary bg-primary/5' : ''}`}
                    data-testid={`btn-estado-${estado.value}`}
                  >
                    {ticket.estado === estado.value && '✓ '}
                    {estado.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Información Adicional</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Fecha creación</dt>
                  <dd className="mt-1">{new Date(ticket.fecha_creacion).toLocaleString('es-ES')}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Fecha preferida</dt>
                  <dd className="mt-1">{new Date(ticket.fecha_preferida_retirada).toLocaleDateString('es-ES')}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Horario</dt>
                  <dd className="mt-1">07:00 - 13:00</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Procedencia</dt>
                  <dd className="mt-1">{ticket.procedencia || 'No especificada'}</dd>
                </div>
                {ticket.hora_visita && (
                  <div>
                    <dt className="font-medium text-gray-500">Hora última visita</dt>
                    <dd className="mt-1 text-orange-700">{ticket.hora_visita}</dd>
                  </div>
                )}
                {ticket.motivo_cancelacion && (
                  <div>
                    <dt className="font-medium text-gray-500">Motivo cancelación</dt>
                    <dd className="mt-1 text-red-700">{ticket.motivo_cancelacion}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* State Change Dialog */}
      <Dialog open={showStateDialog} onOpenChange={setShowStateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="state-change-dialog">
          <DialogHeader>
            <DialogTitle>
              Cambiar estado a: <span className="text-primary">{selectedEstado}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Comment field - always shown */}
            <div>
              <Label htmlFor="comentario">
                Comentario {selectedEstado === 'Realizada' ? '(ej: ha dejado más muebles)' : '(opcional)'}
              </Label>
              <textarea
                id="comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                className="w-full mt-1 p-2 border border-gray-300 rounded-md text-sm"
                rows="3"
                placeholder={
                  selectedEstado === 'Realizada' ? 'Ej: Ha dejado más muebles de los indicados...' :
                  selectedEstado === 'Ausente' ? 'Detalles de la visita...' :
                  selectedEstado === 'Cancelada' ? 'Información adicional...' :
                  'Añadir comentario...'
                }
                data-testid="input-comentario-estado"
              />
            </div>

            {/* Hora visita - only for Ausente */}
            {selectedEstado === 'Ausente' && (
              <div>
                <Label htmlFor="hora_visita">Hora de la visita *</Label>
                <Input
                  id="hora_visita"
                  type="time"
                  value={horaVisita}
                  onChange={(e) => setHoraVisita(e.target.value)}
                  data-testid="input-hora-visita"
                />
                <p className="text-xs text-gray-500 mt-1">Indique a qué hora se realizó la visita</p>
              </div>
            )}

            {/* Motivo cancelación - only for Cancelada */}
            {selectedEstado === 'Cancelada' && (
              <div>
                <Label>Motivo de cancelación *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {MOTIVOS_CANCELACION.map(motivo => (
                    <button
                      key={motivo.value}
                      type="button"
                      onClick={() => setMotivoCancelacion(motivo.value)}
                      className={`p-3 border rounded-lg text-sm font-medium transition-colors duration-150 ${
                        motivoCancelacion === motivo.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`btn-motivo-${motivo.value}`}
                    >
                      {motivo.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStateDialog(false)} data-testid="btn-cancel-state-change">
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdateEstado} 
              disabled={updating}
              data-testid="btn-confirm-state-change"
            >
              {updating ? 'Actualizando...' : 'Confirmar Cambio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTicketDetail;
